/**
 * useSidebarOrchestration — all state, computed values, and callbacks for the
 * main Sidebar component.  Extracted so Sidebar.tsx can be composition-only.
 *
 * Composition (each sub-hook owns its concern):
 *   - useSidebarListAnimations           — auto-animate refs
 *   - useSidebarThreadListExpansion      — per-project thread list expand/collapse
 *   - useSidebarProjectsAndThreads       — project/thread derivation + prewarm
 *   - useSidebarAddProjectFlow           — "add a project" input UX
 *   - useSidebarProjectDnD               — project drag-and-drop
 *   - useSidebarThreadKeyboardShortcuts  — ⌘1..⌘9 + ⌘↑/↓ + jump-hint labels
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import type {
  CollisionDetection,
  DragCancelEvent,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import type { useSensors } from "@dnd-kit/core";
import { type EnvironmentId, type ProjectId, type ScopedThreadRef } from "@cmmd-forge/contracts";
import {
  scopedProjectKey,
  scopedThreadKey,
  scopeProjectRef,
  scopeThreadRef,
} from "@cmmd-forge/client-runtime";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import {
  type SidebarProjectSortOrder,
  type SidebarThreadSortOrder,
} from "@cmmd-forge/contracts/settings";
import { usePrimaryEnvironmentId } from "../../environments/primary";
import { isElectron } from "../../env";
import {
  useEmbedScopedSidebarProjects,
  useEmbedScopedSidebarThreads,
} from "./useEmbedScopedSidebar";
import {
  selectBootstrapCompleteForActiveEnvironment,
  selectProjectsAcrossEnvironments,
  selectSidebarThreadsAcrossEnvironments,
  useStore,
} from "../../store";
import { useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import { selectThreadTerminalState, useTerminalStateStore } from "../../terminalStateStore";
import { useUiStateStore } from "../../uiStateStore";
import { useComposerDraftStore } from "../../composerDraftStore";
import { shortcutLabelForCommand } from "../../keybindings";
import { useNewThreadHandler } from "../../hooks/useHandleNewThread";
import { useThreadActions } from "../../hooks/useThreadActions";
import { buildThreadRouteParams, resolveThreadRouteRef } from "../../threadRoutes";
import {
  shouldClearThreadSelectionOnMouseDown,
  useThreadJumpHintVisibility,
} from "../Sidebar.logic";
import { useSidebarListAnimations } from "./useSidebarListAnimations";
import { useSidebarThreadListExpansion } from "./useSidebarThreadListExpansion";
import { useSidebarProjectsAndThreads } from "./useSidebarProjectsAndThreads";
import { resolveSidebarNewThreadRoute, type SidebarNewThreadRoute } from "./sidebarNewThreadRoute";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import { useSidebarAddProjectFlow } from "./useSidebarAddProjectFlow";
import { useSidebarProjectDnD } from "./useSidebarProjectDnD";
import { useSidebarThreadKeyboardShortcuts } from "./useSidebarThreadKeyboardShortcuts";
import { sortThreads } from "../../lib/threadSort";
import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import { useServerKeybindings } from "../../rpc/serverState";
import { useSavedEnvironmentRegistryStore } from "../../environments/runtime";
import { useSavedEnvironmentRuntimeStore } from "../../environments/runtime/savedEnvironmentRuntimeStore";
import { useThreadSelectionStore } from "../../threadSelectionStore";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";
import type { SidebarProjectWorkspaceIdentity } from "./SidebarProjectItem";
import {
  buildCampaignWorkspaceIndex,
  buildProjectWorkspaceIndex,
  buildTaskWorkspaceIndex,
  buildWorkspaceKeyByTaskTitle,
  resolveWorkspaceKeyFromRef,
  toWorkspaceNameSyntheticId,
} from "./workspaceScoping";
export type { SidebarProjectSnapshot };

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface SidebarOrchestrationResult {
  // Routing
  isOnSettings: boolean;
  pathname: string;
  routeThreadKey: string | null;
  newThreadRoute: SidebarNewThreadRoute;
  // Project / thread data
  sortedProjects: readonly SidebarProjectSnapshot[];
  isManualProjectSorting: boolean;
  expandedThreadListsByProject: ReadonlySet<string>;
  activeRouteProjectKey: string | null;
  projectsLength: number;
  // Shortcut labels
  newThreadShortcutLabel: string | null;
  commandPaletteShortcutLabel: string | null;
  // Thread jump labels
  visibleThreadJumpLabelByKey: ReadonlyMap<string, string>;
  // Project sort / thread sort settings
  projectSortOrder: SidebarProjectSortOrder;
  threadSortOrder: SidebarThreadSortOrder;
  updateSettings: ReturnType<typeof useUpdateSettings>["updateSettings"];
  // Add-project flow
  shouldShowProjectPathEntry: boolean;
  handleStartAddProject: () => void;
  isElectron: boolean;
  // Thread actions
  handleNewThread: ReturnType<typeof useNewThreadHandler>["handleNewThread"];
  archiveThread: ReturnType<typeof useThreadActions>["archiveThread"];
  unarchiveThread: ReturnType<typeof useThreadActions>["unarchiveThread"];
  settleThread: ReturnType<typeof useThreadActions>["settleThread"];
  unsettleThread: ReturnType<typeof useThreadActions>["unsettleThread"];
  deleteThread: ReturnType<typeof useThreadActions>["deleteThread"];
  // Animation refs
  attachThreadListAutoAnimateRef: (node: HTMLElement | null) => void;
  attachProjectListAutoAnimateRef: (node: HTMLElement | null) => void;
  // Expand/collapse
  expandThreadListForProject: (projectKey: string) => void;
  collapseThreadListForProject: (projectKey: string) => void;
  // DnD
  dragInProgressRef: React.RefObject<boolean>;
  suppressProjectClickAfterDragRef: React.RefObject<boolean>;
  suppressProjectClickForContextMenuRef: React.RefObject<boolean>;
  projectDnDSensors: ReturnType<typeof useSensors>;
  projectCollisionDetection: CollisionDetection;
  handleProjectDragEnd: (event: DragEndEvent) => void;
  handleProjectDragStart: (event: DragStartEvent) => void;
  handleProjectDragCancel: (event: DragCancelEvent) => void;
  isBootstrappingLocal: boolean;
}

// ---------------------------------------------------------------------------
// useSidebarOrchestration
// ---------------------------------------------------------------------------

export function useSidebarOrchestration(): SidebarOrchestrationResult {
  const activeEnvironmentId = useStore((store) => store.activeEnvironmentId);
  const environmentStateById = useStore((store) => store.environmentStateById);
  const storeSnapshot = useMemo(
    () => ({ activeEnvironmentId, environmentStateById }),
    [activeEnvironmentId, environmentStateById],
  );
  const allProjects = useMemo(
    () => selectProjectsAcrossEnvironments(storeSnapshot),
    [storeSnapshot],
  );
  // H1: Exclude only the legacy `cmmd-runtime:` locator, which CMMD's runtime inventory re-supplies to ActiveThreadsSection. A durable `env_` project has no such replica, so excluding it by `isCloud` kept it out of the grouping pipeline altogether; it stays here, and splitSidebarProjectsByCloudEnvironment routes it into the Cloud entry.
  // `allProjects` still feeds `buildKnownProjectKeySet` so connected-cloud threads aren't treated as loose.
  const projects = useMemo(
    () =>
      allProjects.filter((p) => classifyEnvironmentId(p.environmentId).legacyRuntimeId === null),
    [allProjects],
  );
  const bootstrapComplete = selectBootstrapCompleteForActiveEnvironment(storeSnapshot);
  const sidebarThreads = useMemo(
    () => selectSidebarThreadsAcrossEnvironments(storeSnapshot),
    [storeSnapshot],
  );
  const { workspaces, tasks: cmmdTasks, projects: cmmdProjects } = useCmmdWorkIntakeSnapshot();
  const projectExpandedById = useUiStateStore((store) => store.projectExpandedById);
  const projectOrder = useUiStateStore((store) => store.projectOrder);
  const reorderProjects = useUiStateStore((store) => store.reorderProjects);
  const navigate = useNavigate();
  const pathname = useLocation({ select: (loc) => loc.pathname });
  const isOnSettings = pathname.startsWith("/settings");
  const sidebarThreadSortOrder = useSettings((s) => s.sidebarThreadSortOrder);
  const sidebarProjectSortOrder = useSettings((s) => s.sidebarProjectSortOrder);
  const defaultThreadEnvMode = useSettings((s) => s.defaultThreadEnvMode);
  const defaultThreadRuntime = useSettings((s) => s.defaultThreadRuntime);
  const { updateSettings } = useUpdateSettings();
  const { handleNewThread } = useNewThreadHandler();
  const { archiveThread, unarchiveThread, settleThread, unsettleThread, deleteThread } =
    useThreadActions();
  const routeThreadRef = useParams({
    strict: false,
    select: (params) => resolveThreadRouteRef(params),
  });
  const routeThreadKey = routeThreadRef ? scopedThreadKey(routeThreadRef) : null;
  // `resolveThreadRouteRef` only answers for SERVER threads; a draft route
  // carries neither environmentId nor threadId, so "New thread in <project>"
  // recovers the project from the draft session instead (CMMD-2266 — without
  // this /new stayed pinned to a stale project).
  const routeDraftId = useParams({
    strict: false,
    select: (params: Partial<Record<"draftId", string>>) => params.draftId ?? null,
  });
  const routeDraftSession = useComposerDraftStore((store) =>
    routeDraftId ? store.getDraftSession(routeDraftId as never) : null,
  );
  const keybindings = useServerKeybindings();
  const { expandedThreadListsByProject, expandThreadListForProject, collapseThreadListForProject } =
    useSidebarThreadListExpansion();
  const { showThreadJumpHints, updateThreadJumpHintsVisibility } = useThreadJumpHintVisibility();
  const suppressProjectClickForContextMenuRef = useRef(false);
  const selectedThreadCount = useThreadSelectionStore((s) => s.selectedThreadKeys.size);
  const clearSelection = useThreadSelectionStore((s) => s.clearSelection);
  const setSelectionAnchor = useThreadSelectionStore((s) => s.setAnchor);
  const platform = navigator.platform;
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const savedEnvironmentRegistry = useSavedEnvironmentRegistryStore((s) => s.byId);
  const savedEnvironmentRuntimeById = useSavedEnvironmentRuntimeStore((s) => s.byId);
  const workspaceIdentityByKey = useMemo(() => {
    const map = new Map<string, SidebarProjectWorkspaceIdentity>();
    const register = (identity: SidebarProjectWorkspaceIdentity) => {
      map.set(identity.workspaceKey, identity);
      if (identity.workspaceId) map.set(identity.workspaceId, identity);
      map.set(toWorkspaceNameSyntheticId(identity.name), identity);
    };
    for (const workspace of workspaces) {
      register({
        workspaceKey: workspace.id,
        workspaceId: workspace.id,
        name: workspace.name,
        color: workspace.color ?? null,
        iconUrl: workspace.iconUrl ?? workspace.logoUrl ?? null,
      });
    }
    const registerFallback = (input: {
      readonly workspaceId?: string | null;
      readonly workspaceName?: string | null;
    }) => {
      const workspaceKey = resolveWorkspaceKeyFromRef({
        workspaceId: input.workspaceId ?? null,
        workspaceName: input.workspaceName ?? null,
      });
      const workspaceName = input.workspaceName?.trim();
      if (!workspaceKey || !workspaceName || map.has(workspaceKey)) return;
      register({
        workspaceKey,
        workspaceId: input.workspaceId ?? null,
        name: workspaceName,
        color: null,
        iconUrl: null,
      });
    };
    for (const project of cmmdProjects) registerFallback(project);
    for (const task of cmmdTasks) registerFallback(task);
    return map;
  }, [cmmdProjects, cmmdTasks, workspaces]);

  const projectWorkspaceIndex = useMemo(
    () => buildProjectWorkspaceIndex(cmmdProjects),
    [cmmdProjects],
  );
  const taskWorkspaceIndex = useMemo(
    () => buildTaskWorkspaceIndex(cmmdTasks, projectWorkspaceIndex),
    [cmmdTasks, projectWorkspaceIndex],
  );
  const campaignWorkspaceIndex = useMemo(
    () => buildCampaignWorkspaceIndex(cmmdProjects),
    [cmmdProjects],
  );
  const workspaceKeyByTaskTitle = useMemo(
    () => buildWorkspaceKeyByTaskTitle(cmmdTasks, projectWorkspaceIndex),
    [cmmdTasks, projectWorkspaceIndex],
  );
  const {
    embedMode,
    embedWorkspaceId,
    scopedSidebarThreads,
    workspaceIdentityByPhysicalProjectKey,
  } = useEmbedScopedSidebarThreads({
    sidebarThreads,
    taskWorkspaceIndex,
    projectWorkspaceIndex,
    campaignWorkspaceIndex,
    workspaceKeyByTaskTitle,
    workspaceIdentityByKey,
  });

  const {
    physicalToLogicalKey,
    sidebarProjects,
    sidebarThreadByKey,
    threadsByProjectKey,
    activeRouteProjectKey,
    activeRouteProjectRef,
    sortedProjects,
    isManualProjectSorting,
    visibleSidebarThreadKeys,
    threadJumpCommandByKey,
    threadJumpThreadKeys,
  } = useSidebarProjectsAndThreads({
    projects,
    sidebarThreads: scopedSidebarThreads,
    projectOrder,
    projectExpandedById,
    expandedThreadListsByProject,
    primaryEnvironmentId,
    savedEnvironmentRegistry,
    savedEnvironmentRuntimeById,
    workspaceIdentityByPhysicalProjectKey,
    sidebarProjectSortOrder,
    sidebarThreadSortOrder,
    routeThreadKey,
  });

  const { embedScopedSortedProjects, embedScopedSidebarProjects } = useEmbedScopedSidebarProjects({
    embedMode,
    embedWorkspaceId,
    sortedProjects,
    sidebarProjects,
  });

  const getCurrentSidebarShortcutContext = useCallback(
    () => ({
      terminalFocus: false,
      terminalOpen: routeThreadRef
        ? selectThreadTerminalState(
            useTerminalStateStore.getState().terminalStateByThreadKey,
            routeThreadRef,
          ).terminalOpen
        : false,
    }),
    [routeThreadRef],
  );

  const newThreadShortcutLabelOptions = useMemo(
    () => ({ platform, context: { terminalFocus: false, terminalOpen: false } }),
    [platform],
  );

  const newThreadShortcutLabel =
    shortcutLabelForCommand(keybindings, "chat.newLocal", newThreadShortcutLabelOptions) ??
    shortcutLabelForCommand(keybindings, "chat.new", newThreadShortcutLabelOptions);

  const focusMostRecentThreadForProject = useCallback(
    (projectRef: { environmentId: EnvironmentId; projectId: ProjectId }) => {
      const physicalKey = scopedProjectKey(
        scopeProjectRef(projectRef.environmentId, projectRef.projectId),
      );
      const logicalKey = physicalToLogicalKey.get(physicalKey) ?? physicalKey;
      const latestThread = sortThreads(
        (threadsByProjectKey.get(logicalKey) ?? []).filter((thread) => thread.archivedAt === null),
        sidebarThreadSortOrder,
      )[0];
      if (!latestThread) return;
      void navigate({
        to: "/$environmentId/$threadId",
        params: buildThreadRouteParams(scopeThreadRef(latestThread.environmentId, latestThread.id)),
      });
    },
    [sidebarThreadSortOrder, navigate, threadsByProjectKey, physicalToLogicalKey],
  );

  const { shouldShowProjectPathEntry, handleStartAddProject } = useSidebarAddProjectFlow({
    activeEnvironmentId,
    primaryEnvironmentId,
    projects,
    defaultThreadRuntime,
    defaultThreadEnvMode,
    handleNewThread,
    focusMostRecentThreadForProject,
  });

  const navigateToThread = useCallback(
    (threadRef: ScopedThreadRef) => {
      if (useThreadSelectionStore.getState().selectedThreadKeys.size > 0) clearSelection();
      setSelectionAnchor(scopedThreadKey(threadRef));
      void navigate({
        to: "/$environmentId/$threadId",
        params: buildThreadRouteParams(threadRef),
      });
    },
    [clearSelection, navigate, setSelectionAnchor],
  );

  const {
    projectDnDSensors,
    projectCollisionDetection,
    dragInProgressRef,
    suppressProjectClickAfterDragRef,
    handleProjectDragStart,
    handleProjectDragEnd,
    handleProjectDragCancel,
  } = useSidebarProjectDnD({
    sidebarProjects: embedScopedSidebarProjects,
    sidebarProjectSortOrder,
    reorderProjects,
  });

  const { attachProjectListAutoAnimateRef, attachThreadListAutoAnimateRef } =
    useSidebarListAnimations();

  const { visibleThreadJumpLabelByKey, commandPaletteShortcutLabel } =
    useSidebarThreadKeyboardShortcuts({
      keybindings,
      platform,
      orderedSidebarThreadKeys: visibleSidebarThreadKeys,
      threadJumpCommandByKey,
      threadJumpThreadKeys,
      sidebarThreadByKey,
      routeThreadKey,
      navigateToThread,
      getCurrentSidebarShortcutContext,
      showThreadJumpHints,
      updateThreadJumpHintsVisibility,
      newThreadShortcutLabelOptions,
    });

  useEffect(() => {
    const onMouseDown = (event: globalThis.MouseEvent) => {
      if (selectedThreadCount === 0) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!shouldClearThreadSelectionOnMouseDown(target)) return;
      clearSelection();
    };
    window.addEventListener("mousedown", onMouseDown);
    return () => window.removeEventListener("mousedown", onMouseDown);
  }, [clearSelection, selectedThreadCount]);

  // The sidebar header's "New thread" target — the one answer to "which
  // project is the route in?" across both route kinds, so the composer opens
  // on the user's actual project, not the drag order's first row (CMMD-2266).
  const activeProjectRef = useMemo(() => {
    if (activeRouteProjectRef) return activeRouteProjectRef;
    if (!routeDraftSession) return null;
    return {
      environmentId: routeDraftSession.environmentId,
      projectId: routeDraftSession.projectId,
    };
  }, [activeRouteProjectRef, routeDraftSession]);

  const newThreadRoute = useMemo(
    () => resolveSidebarNewThreadRoute(activeProjectRef),
    [activeProjectRef],
  );

  // For `/new` navigations with no project in the URL — otherwise the
  // landing falls back to sidebar drag order instead of where the user was.
  const setLastActiveProject = useUiStateStore((store) => store.setLastActiveProject);
  useEffect(() => {
    if (!activeProjectRef) return;
    setLastActiveProject(activeProjectRef);
  }, [activeProjectRef, setLastActiveProject]);

  return {
    isOnSettings,
    pathname,
    routeThreadKey,
    newThreadRoute,
    sortedProjects: embedScopedSortedProjects,
    isManualProjectSorting,
    expandedThreadListsByProject,
    activeRouteProjectKey,
    projectsLength: embedScopedSortedProjects.length,
    newThreadShortcutLabel,
    commandPaletteShortcutLabel,
    visibleThreadJumpLabelByKey,
    projectSortOrder: sidebarProjectSortOrder,
    threadSortOrder: sidebarThreadSortOrder,
    updateSettings,
    shouldShowProjectPathEntry,
    handleStartAddProject,
    isElectron,
    handleNewThread,
    archiveThread,
    unarchiveThread,
    settleThread,
    unsettleThread,
    deleteThread,
    attachThreadListAutoAnimateRef,
    attachProjectListAutoAnimateRef,
    expandThreadListForProject,
    collapseThreadListForProject,
    dragInProgressRef,
    suppressProjectClickAfterDragRef,
    suppressProjectClickForContextMenuRef,
    projectDnDSensors,
    projectCollisionDetection,
    handleProjectDragEnd,
    handleProjectDragStart,
    handleProjectDragCancel,
    isBootstrappingLocal: !bootstrapComplete,
  };
}
